import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import useReveal from "@/hooks/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const ref = useReveal();
  const [form, setForm] = useState({
    name: "",
    email: "",
    instagram: "",
    monthly_revenue: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Name, email and message are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Application received. I'll be in touch.");
      setSubmitted(true);
      setForm({ name: "", email: "", instagram: "", monthly_revenue: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      ref={ref}
      className="reveal relative px-6 md:px-12 py-28 md:py-40 bg-[#0A0A0A] border-t border-[#2E2A28]"
    >
      <div className="max-w-6xl mx-auto grid grid-cols-12 gap-10">
        <div className="col-span-12 md:col-span-5">
          <div className="overline section-mark mb-6">Apply</div>
          <h2 className="font-serif-display text-4xl md:text-6xl font-light leading-[1] tracking-tight text-[#F2E7DF]">
            Ready to build? <br />
            <span className="italic text-[#E5C16C]">Let&apos;s talk.</span>
          </h2>
          <p className="mt-8 text-[#A19D9B] text-base md:text-lg max-w-md leading-relaxed">
            I take on a maximum of two new operators per quarter. Tell me about
            your audience, your current revenue, and what you&apos;re trying to launch.
          </p>
          <div className="mt-10 space-y-3 font-mono-detail text-xs tracking-[0.18em] uppercase text-[#A19D9B]">
            <div>Response — within 48 hours</div>
            <div>Discretion — guaranteed</div>
          </div>
        </div>

        <form
          data-testid="contact-form"
          onSubmit={onSubmit}
          className="col-span-12 md:col-span-7 field-line space-y-8 bg-[#141414] border border-[#2E2A28] p-8 md:p-12"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div>
              <label htmlFor="c-name">Name</label>
              <input
                id="c-name"
                data-testid="contact-form-name"
                type="text"
                value={form.name}
                onChange={update("name")}
                placeholder="Your full name"
                required
              />
            </div>
            <div>
              <label htmlFor="c-email">Email</label>
              <input
                id="c-email"
                data-testid="contact-form-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="you@brand.com"
                required
              />
            </div>
            <div>
              <label htmlFor="c-ig">Instagram Handle</label>
              <input
                id="c-ig"
                data-testid="contact-form-instagram"
                type="text"
                value={form.instagram}
                onChange={update("instagram")}
                placeholder="@yourhandle"
              />
            </div>
            <div>
              <label htmlFor="c-rev">Current Monthly Revenue</label>
              <input
                id="c-rev"
                data-testid="contact-form-revenue"
                type="text"
                value={form.monthly_revenue}
                onChange={update("monthly_revenue")}
                placeholder="$0 – $10k / $10k+ / etc."
              />
            </div>
          </div>
          <div>
            <label htmlFor="c-msg">Your Situation</label>
            <textarea
              id="c-msg"
              data-testid="contact-form-message"
              value={form.message}
              onChange={update("message")}
              placeholder="Audience size, niche, what you've tried, what you want to launch…"
              required
            />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4 pt-4">
            <span className="font-mono-detail text-[0.65rem] tracking-[0.22em] uppercase text-[#A19D9B]">
              {submitted ? "Application received" : "All applications reviewed personally"}
            </span>
            <button
              type="submit"
              data-testid="contact-form-submit"
              disabled={submitting}
              className="btn-burgundy"
            >
              {submitting ? "Sending…" : "Apply Now"}
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
